import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { source } from "../../Constants/source";
import NextIconButton from "./NextIconButton";
import PrevIconButton from "./PrevIconButton";
import LISTINGSIMAGE from "../../assets/ListingsImage.jpg";
import APPOINTMENTDEFAULTIMAGE from "../../assets/Appointments.png";
import { ClipLoader } from "react-spinners";
import mediaType from "../../Constants/mediaType.js";

const extractSafeData = (data = [],currentIndex,imagesPerPage ) => {
    const visibleImages = data.slice(currentIndex, currentIndex + imagesPerPage);
    const remainingImages = data.length - (currentIndex + imagesPerPage);
    return [visibleImages, remainingImages]
}

const CustomCarousel = ({ imageList, sourceId, appointmentId, isImageAsBlob=false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});

  const imagesPerPage = 4;

  const handleImageLoad = (index) => {
    setLoadedImages((prev) => ({ ...prev, [index]: true }));
  };

  const nextImages = () => {
    if (currentIndex + imagesPerPage < imageList.length) {
      setCurrentIndex(currentIndex + imagesPerPage);
    }
  };

  const prevImages = () => {
    if (currentIndex - imagesPerPage >= 0) {
      setCurrentIndex(currentIndex - imagesPerPage);
    }
  };

  const [selectedImage, setSelectedImage] = useState(null);
  const [safeImageList,visibleImages ,remainingImages] = useMemo(()=>{
    const safeImageLists = isImageAsBlob ? imageList : (imageList || []).filter(item => item.mediaType === 1);
    const safeData = extractSafeData(safeImageLists,currentIndex,imagesPerPage);
    return [safeImageLists, ...safeData]
  },[isImageAsBlob,imageList,currentIndex,imagesPerPage])

  const openImage = (img) => setSelectedImage(img);
  const closeImage = () => setSelectedImage(null);

  const getImagePath = (mediaItem) => {
    if(isImageAsBlob) return mediaItem
    if (!mediaItem || mediaItem.mediaType !== mediaType.IMAGE) return null;

    if (mediaItem.signedUrl) {
      return mediaItem.signedUrl;
    }

    let path = mediaItem.path;
    if (sourceId === source.User) {
      path = process.env.REACT_APP_MEDIA_URL + path;
    }
    return path;
  };

  return (
    <div className="relative w-full flex flex-col items-center">
      {safeImageList.length === 0 ? (
        <img
          src={appointmentId ? APPOINTMENTDEFAULTIMAGE : LISTINGSIMAGE}
          alt="Default"
          className="object-cover w-full h-[400px] md:h-[500px] rounded-lg shadow-md"
        />
      ) : (
        <>
          {/* Single Image */}
          {visibleImages.length === 1 && (
            <div className="relative">
              {!loadedImages[0] && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 rounded-lg">
                  <ClipLoader size={30} color="#36D7B7" />
                </div>
              )}
              <img
                onClick={() => openImage(getImagePath(visibleImages[0]))}
                src={getImagePath(visibleImages[0])}
                alt="Main image"
                className={`object-cover w-full h-[400px] md:h-[500px] rounded-lg shadow-md cursor-pointer transition-opacity duration-300 ${loadedImages[0] ? "opacity-100" : "opacity-0"}`}
                onLoad={() => handleImageLoad(0)}
                onError={() => handleImageLoad(0)}
              />
            </div>
          )}

          {/* Two Images */}
          {visibleImages.length === 2 && (
            <div className="grid grid-cols-2 gap-2 w-full">
              {visibleImages.map((item, i) => (
                <div key={i} className="relative">
                  {!loadedImages[i] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 rounded-lg">
                      <ClipLoader size={30} color="#36D7B7" />
                    </div>
                  )}
                  <img
                    onClick={() => openImage(getImagePath(item))}
                    src={getImagePath(item)}
                    alt={`Image ${i}`}
                    className={`object-cover w-full h-[300px] md:h-[400px] rounded-lg shadow-md cursor-pointer transition-opacity duration-300 ${loadedImages[i] ? "opacity-100" : "opacity-0"}`}
                    onLoad={() => handleImageLoad(i)}
                    onError={() => handleImageLoad(i)}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Three Images */}
          {visibleImages.length === 3 && (
            <div className="grid grid-cols-2 gap-2 w-full">
              <div className="relative">
                {!loadedImages[0] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 rounded-lg">
                    <ClipLoader size={30} color="#36D7B7" />
                  </div>
                )}
                <img
                  onClick={() => openImage(getImagePath(visibleImages[0]))}
                  src={getImagePath(visibleImages[0])}
                  alt="Main image"
                  className={`object-cover w-full h-[408px] md:h-[508px] rounded-lg shadow-md cursor-pointer transition-opacity duration-300 ${loadedImages[0] ? "opacity-100" : "opacity-0"}`}
                  onLoad={() => handleImageLoad(0)}
                  onError={() => handleImageLoad(0)}
                />
              </div>
              <div className="grid grid-rows-2 gap-2">
                {visibleImages.slice(1, 3).map((item, i) => (
                  <div key={i + 1} className="relative">
                    {!loadedImages[i + 1] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 rounded-lg">
                        <ClipLoader size={30} color="#36D7B7" />
                      </div>
                    )}
                    <img
                      onClick={() => openImage(getImagePath(item))}
                      src={getImagePath(item)}
                      alt={`Small image ${i + 1}`}
                      className={`object-cover w-full h-[200px] md:h-[250px] rounded-lg shadow-md cursor-pointer transition-opacity duration-300 ${loadedImages[i + 1] ? "opacity-100" : "opacity-0"}`}
                      onLoad={() => handleImageLoad(i + 1)}
                      onError={() => handleImageLoad(i + 1)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Four or More Images */}
          {visibleImages.length >= 4 && (
            <div className="grid grid-cols-2 grid-rows-2 gap-2 w-full">
              <div className="row-span-2">
                <div className="relative">
                  {!loadedImages[0] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 rounded-lg">
                      <ClipLoader size={30} color="#36D7B7" />
                    </div>
                  )}
                  <img
                    onClick={() => openImage(getImagePath(visibleImages[0]))}
                    src={getImagePath(visibleImages[0])}
                    alt="Main image"
                    className={`object-cover w-full h-[308px] md:h-[408px] rounded-lg shadow-md cursor-pointer transition-opacity duration-300 ${loadedImages[0] ? "opacity-100" : "opacity-0"}`}
                    onLoad={() => handleImageLoad(0)}
                    onError={() => handleImageLoad(0)}
                  />
                </div>
              </div>
              <div className="relative">
                {!loadedImages[1] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 rounded-lg">
                    <ClipLoader size={30} color="#36D7B7" />
                  </div>
                )}
                <img
                  onClick={() => openImage(getImagePath(visibleImages[1]))}
                  src={getImagePath(visibleImages[1])}
                  alt="Side image"
                  className={`object-cover w-full h-[150px] md:h-[200px] rounded-lg shadow-md cursor-pointer transition-opacity duration-300 ${loadedImages[1] ? "opacity-100" : "opacity-0"}`}
                  onLoad={() => handleImageLoad(1)}
                  onError={() => handleImageLoad(1)}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {visibleImages.slice(2, 4).map((item, i) => {
                  const imageKey = i + 2;
                  return (
                    <div key={imageKey} className="relative">
                      {!loadedImages[imageKey] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 rounded-lg">
                          <ClipLoader size={30} color="#36D7B7" />
                        </div>
                      )}
                      <img
                        onClick={() => openImage(getImagePath(item))}
                        src={getImagePath(item)}
                        alt={`Small image ${imageKey}`}
                        className={`object-cover w-full h-[150px] md:h-[200px] rounded-lg shadow-md cursor-pointer transition-opacity duration-300 ${loadedImages[imageKey] ? "opacity-100" : "opacity-0"}`}
                        onLoad={() => handleImageLoad(imageKey)}
                        onError={() => handleImageLoad(imageKey)}
                      />
                      {i === 1 && remainingImages > 0 && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white text-lg font-bold rounded-lg">
                          +{remainingImages}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Full screen modal */}
          {selectedImage && (
            <div
              className="fixed inset-0 bg-black p-10 bg-opacity-80 z-50 flex items-center justify-center"
              onClick={closeImage}
            >
              <img
                src={selectedImage}
                alt="Full size"
                className="max-w-full max-h-full rounded-lg shadow-lg"
              />
              <button
                onClick={closeImage}
                className="absolute top-4 right-4 text-white text-2xl font-bold"
              >
                ×
              </button>
            </div>
          )}

          {/* Navigation */}
          {safeImageList.length > imagesPerPage && (
            <div className="flex justify-center gap-2 w-full max-w-5xl mt-4">
              <PrevIconButton onClick={prevImages} disabled={currentIndex === 0} />
              <NextIconButton
                onClick={nextImages}
                disabled={currentIndex + imagesPerPage >= safeImageList.length}
              />
            </div>
          )}
        </>
      )}
    </div>

  );
};

CustomCarousel.propTypes = {
  imageList: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      mediaOrder: PropTypes.number,
    })
  ),
  sourceId: PropTypes.number.isRequired,
  appointmentId: PropTypes.number,
};

export default CustomCarousel;